import React from 'react';

interface SignInButtonProps {
  provider: 'google' | 'facebook';
  style?: React.CSSProperties;
}

const PROVIDER_CONFIG = {
  google: {
    label: 'Sign in with Google',
    href: '/api/auth/google',
    background: '#ffffff',
    color: '#1f1f1f',
    borderColor: '#dadce0',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.467-.806 5.956-2.185l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        />
        <path
          fill="#FBBC05"
          d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"
        />
      </svg>
    ),
  },
  facebook: {
    label: 'Sign in with Facebook',
    href: '/api/auth/facebook',
    background: '#1877F2',
    color: '#ffffff',
    borderColor: '#1877F2',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
        <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.791-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.887v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
      </svg>
    ),
  },
} as const;

/**
 * OAuth sign-in button. Navigates directly to the backend auth route,
 * which handles the full redirect-based OAuth flow.
 * Designed for 10" tablet — large tap target, minimum 52px height.
 */
export function SignInButton({ provider, style }: SignInButtonProps) {
  const cfg = PROVIDER_CONFIG[provider];

  return (
    <a
      href={cfg.href}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '14px 20px',
        minHeight: '52px',
        fontSize: '1rem',
        fontWeight: 600,
        textDecoration: 'none',
        borderRadius: '10px',
        background: cfg.background,
        color: cfg.color,
        border: `1.5px solid ${cfg.borderColor}`,
        cursor: 'pointer',
        touchAction: 'manipulation',
        letterSpacing: '0.01em',
        transition: 'opacity 0.15s ease',
        boxSizing: 'border-box',
        ...style,
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.88')}
      onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
    >
      {cfg.icon}
      <span>{cfg.label}</span>
    </a>
  );
}
